export function removeDuplicatedFilterOperators(filterOperators) {
  const result = [];
  for (const filterOperator of filterOperators) {
    if (result.length === 0) {
      result.push(filterOperator);
      continue;
    }
    if (result.some(filterOperatorInResult => isSameFilterOperator(filterOperatorInResult, filterOperator))) {
      continue;
    }
    result.push(filterOperator);
  }
  return result;
}

/**
 * Compare two filter operators.
 * @param filterOperatorA
 * @param filterOperatorB
 * @returns true when both are the exact same, false otherwise.
 */
export function isSameFilterOperator(filterOperatorA, filterOperatorB) {
  if (filterOperatorA.operator !== filterOperatorB.operator || filterOperatorA.property !== filterOperatorB.property) {
    return false;
  }

  /**
   * Operators that don't have @value
   */
  if (filterOperatorA.operator === 'HAS_PROPERTY' || filterOperatorA.operator === 'NOT_HAS_PROPERTY') {
    return true;
  }
  if (filterOperatorA.operator === 'BETWEEN' && filterOperatorB.operator === 'BETWEEN') {
    return filterOperatorA.value === filterOperatorB.value && filterOperatorA.highValue === filterOperatorB.highValue;
  }
  if (filterOperatorA.operator === 'ROLLING_DATE_RANGE' && filterOperatorB.operator === 'ROLLING_DATE_RANGE' || filterOperatorA.operator === 'TIME_UNIT_TO_DATE' && filterOperatorB.operator === 'TIME_UNIT_TO_DATE') {
    return filterOperatorA.inclusive === filterOperatorB.inclusive && filterOperatorA.rollForward === filterOperatorB.rollForward && filterOperatorA.rollingOffset === filterOperatorB.rollingOffset && filterOperatorA.timeUnit === filterOperatorB.timeUnit && filterOperatorA.timeUnitCount === filterOperatorB.timeUnitCount;
  }

  /**
   * Operators that have @value
   * Covers: EQ, GT, GTE, LT, LTE
   */
  if ('value' in filterOperatorA && 'value' in filterOperatorB) {
    return filterOperatorA.value === filterOperatorB.value;
  }

  /**
   * Operators that have @values
   * Covers: IN, NOT_IN
   */
  if ('values' in filterOperatorA && 'values' in filterOperatorB) {
    const {
      values: valuesA
    } = filterOperatorA;
    const {
      values: valuesB
    } = filterOperatorB;
    return valuesA.length === valuesB.length && valuesA.every(item => valuesB.includes(item)) && valuesB.every(item => valuesA.includes(item));
  }
  return false;
}
export function areSameFilterOperators(filterGroupA, filterGroupB) {
  if (filterGroupA.length !== filterGroupB.length) {
    return false;
  }
  return filterGroupA.every(filterOperatorA => filterGroupB.some(filterOperatorB => isSameFilterOperator(filterOperatorA, filterOperatorB)));
}
export function isSameFilterGroup(filterGroupA, filterGroupB) {
  if (filterGroupA.filters.length !== filterGroupB.filters.length) {
    return false;
  }
  return filterGroupA.filters.every(filterOperatorA => filterGroupB.filters.some(filterOperatorB => isSameFilterOperator(filterOperatorA, filterOperatorB)));
}
export function areSameFilterGroups(filterGroupsA, filterGroupsB) {
  if (isEmptyFilterGroup(filterGroupsA) && isEmptyFilterGroup(filterGroupsB)) {
    return true;
  }
  if (filterGroupsA.length !== filterGroupsB.length) {
    return false;
  }
  return filterGroupsA.every(filterGroupA => filterGroupsB.some(filterGroupB => isSameFilterGroup(filterGroupA, filterGroupB)));
}

/**
 * Returns true when all filter operators in @filterGroupA exist in @filterGroupB
 */
export function containsFilterGroup(filterGroupA, filterGroupB) {
  return filterGroupA.filters.every(filterOperatorA => filterGroupB.filters.some(filterOperatorB => isSameFilterOperator(filterOperatorA, filterOperatorB)));
}
export function isEmptyFilterGroup(filterGroups) {
  if (filterGroups.length === 0) {
    return true;
  }
  return filterGroups.every(filterGroup => filterGroup.filters.length === 0);
}

/**
 * Remove an entire filter group from the source @filterGroups.
 * Will only remove filter groups where all filter operators are present ➡️ partial filter groups won't be taken into account.
 * @param filterGroups The source filter groups
 * @param filterGroupsToRemove The filter groups to be removed
 */
export function removeFilterGroups(filterGroups, filterGroupsToRemove) {
  let result = [...filterGroups];
  for (const filterGroupToRemove of filterGroupsToRemove) {
    /**
     * Match each group with its own index so we can easily remove filter operators later on
     */
    let filterGroupsWithIndex = result.map((filterGroup, index) => ({
      index,
      filterGroup
    }));

    /**
     * Keep only groups that contain all elements in @filterGroupsToRemove
     */
    filterGroupsWithIndex = filterGroupsWithIndex.filter(filterGroupWithIndex => {
      return containsFilterGroup(filterGroupToRemove, filterGroupWithIndex.filterGroup);
    });

    /**
     * From the result above, update @result by removing all items in @filterGroupToRemove
     */
    for (const filterGroupWithIndex of filterGroupsWithIndex) {
      const {
        filterGroup,
        index
      } = filterGroupWithIndex;
      result[index] = Object.assign({}, result[index], {
        filters: filterGroup.filters.filter(filterOperator => {
          return !filterGroupToRemove.filters.some(filterOperatorToRemove => isSameFilterOperator(filterOperatorToRemove, filterOperator));
        })
      });
    }

    /**
     * Ignore empty filter groups
     */
    result = result.filter(fg => !isEmptyFilterGroup([fg]));
  }
  if (result.length === 0) {
    return [{
      filters: []
    }];
  }
  return result;
}

/**
 * Combines @customFilters to @appFilters.
 * Useful when applying hiddenFilter config to all filter conditions in the UI
 * @param appFilters
 * @param customFilters
 * @returns
 */
export function combineFilters(appFilters, customFilters) {
  if (isEmptyFilterGroup(customFilters)) {
    return appFilters;
  }
  if (isEmptyFilterGroup(appFilters)) {
    return customFilters;
  }
  const result = [];
  for (const appFilterGroup of appFilters) {
    for (const customFilterGroup of customFilters) {
      result.push({
        filters: removeDuplicatedFilterOperators([...appFilterGroup.filters, ...customFilterGroup.filters])
      });
    }
  }
  return result;
}
export function getAggregatedFilterGroups({
  deletedObjects,
  filterGroups,
  hiddenFilter,
  includeHiddenFilter,
  quickFilters
}) {
  let aggregatedFilterGroups = isEmptyFilterGroup(filterGroups) ? [{
    filters: []
  }] : [...filterGroups];

  // Merge with @deletedObjects
  aggregatedFilterGroups = combineFilters(aggregatedFilterGroups, [{
    filters: Array.isArray(deletedObjects) && deletedObjects.length > 0 ? [{
      operator: 'NOT_IN',
      property: 'hs_object_id',
      values: deletedObjects
    }] : []
  }]);

  // Merge with quickFilters
  if (quickFilters.length) {
    aggregatedFilterGroups = combineFilters(aggregatedFilterGroups, [{
      filters: quickFilters
    }]);
  }

  // Merge with hidden filters
  if (hiddenFilter && includeHiddenFilter) {
    aggregatedFilterGroups = combineFilters(aggregatedFilterGroups, hiddenFilter.filterGroups);
  }

  /**
   * Make sure we don't leak hidden filters that exist in @filterGroups
   */
  if (hiddenFilter && !includeHiddenFilter) {
    const combinedFilters = combineFilters(aggregatedFilterGroups, hiddenFilter.filterGroups);
    aggregatedFilterGroups = removeFilterGroups(combinedFilters, hiddenFilter.filterGroups);
  }
  return aggregatedFilterGroups;
}